import { useMemo } from 'react';
import { usePageTour } from '@components/PageTour/usePageTour';
import {
  LOGISTICS_TOUR_ID,
  LOGISTICS_TOUR_STEPS,
  LOGISTICS_VIEWER_TOUR_STEPS,
  type ILogisticsTourContext,
} from './logisticsTour';

interface IUseLogisticsTourArgs {
  /** The page's data is in and the panels are rendered. */
  ready: boolean;
  /** A delivery or stock drawer is open (a deep link lands with one). */
  drawerOpen: boolean;
  /** The caller may write — a viewer reads the steps' read-only copy. */
  canEdit: boolean;
  hasNewDeliveryButton: boolean;
  /** Closes the order dialog the action step opened. */
  closeOrderDialog: () => void;
}

/** The Logistics tour's seam on the page: the shared lifecycle plus the
 *  context its script reads. A dismissal closes the order dialog. The tour
 *  waits while a drawer is open — it would spotlight panels the drawer
 *  covers — and opens once it closes. */
export function useLogisticsTour({
  ready,
  drawerOpen,
  canEdit,
  hasNewDeliveryButton,
  closeOrderDialog,
}: IUseLogisticsTourArgs) {
  const { open, dismiss, key } = usePageTour(LOGISTICS_TOUR_ID, { ready: ready && !drawerOpen, onDismiss: closeOrderDialog });
  const context = useMemo<ILogisticsTourContext>(
    () => ({ hasNewDeliveryButton, close: closeOrderDialog }),
    [hasNewDeliveryButton, closeOrderDialog],
  );
  return { open, dismiss, key, context, steps: canEdit ? LOGISTICS_TOUR_STEPS : LOGISTICS_VIEWER_TOUR_STEPS };
}
